// Lee el archivo de prepedidos del cliente ("BASE DE DATOS") y lo convierte en
// pedidos capturables. Un renglon del Excel = un modelo dentro de un prepedido,
// con sus colores en una sola celda y la escala de tallas en texto (o las
// cantidades en las columnas 28..42).
//
// Reglas que salieron de los datos reales:
//   - El codigo del articulo es MODELO; REFERENCIA DE MUESTRA es el codigo del
//     cliente (se repite entre modelos, por eso no sirve de llave).
//   - Un prepedido es de un cliente; si mezcla proveedores, al generar la orden
//     de compra ya sale una por proveedor.
//   - CANTIDAD es el total del renglon: se reparte entre las tallas segun la
//     proporcion y cada talla entre los colores. El total nunca cambia.
//   - El color viene sucio ("ROSa 14-1905 tcx", "HUESO (MUESTRA)"): se limpia el
//     nombre y la referencia pantone se guarda aparte.
//   - Sin escala de tallas se usa NA y el renglon se marca para revisarlo.
const COLUMNAS_TALLA = ["28", "30", "32", "34", "36", "38", "40", "42"];
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").replace(/[$,\s]/g, "")) || 0;
// Fecha del Excel a "YYYY-MM-DD": llega como numero de serie de Excel (46310),
// como texto dd/mm/aaaa o aaaa-mm-dd, o vacia ("" si no se entiende).
export function fechaISO(v) {
  if (v === undefined || v === null || v === "") return "";
  const dos = (n) => String(n).padStart(2, "0");
  if (typeof v === "number" || /^\d{5}(\.\d+)?$/.test(String(v).trim())) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.round(Number(v)) * 86400000);
    return `${d.getUTCFullYear()}-${dos(d.getUTCMonth() + 1)}-${dos(d.getUTCDate())}`;
  }
  if (v instanceof Date && !isNaN(v)) return `${v.getFullYear()}-${dos(v.getMonth() + 1)}-${dos(v.getDate())}`;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${dos(m[2])}-${dos(m[3])}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) return `${m[3].length === 2 ? "20" + m[3] : m[3]}-${dos(m[2])}-${dos(m[1])}`;
  return "";
}

// Busca el valor de una columna aunque el encabezado traiga espacios de sobra
// o acentos distintos ("# PREPEDIDO ", " COSTO FOB ").
const norm = (v) => txt(v).toUpperCase().replace(/\*/g, "").replace(/\s+/g, " ").trim()
  .normalize("NFD").replace(/[̀-ͯ]/g, "");
function dame(fila, nombre) {
  const buscado = norm(nombre);
  const k = Object.keys(fila).find((x) => norm(x) === buscado);
  return k ? fila[k] : "";
}

// "ROSa 14-1905 tcx" -> { nombre: "ROSA", pantone: "14-1905 TCX" }
// "HUESO (MUESTRA)"  -> { nombre: "HUESO", muestra: true }
export function limpiaColor(crudo) {
  let s = txt(crudo);
  if (!s) return null;
  const muestra = /\(\s*muestra\s*\)/i.test(s);
  s = s.replace(/\(\s*muestra\s*\)/ig, " ");
  const pantones = [];
  s = s.replace(/(\d{2}\s*[-.]\s*\d{3,4})\s*tcx/ig, (_, ref) => {
    pantones.push(`${ref.replace(/\s/g, "").replace(".", "-")} TCX`);
    return " ";
  });
  const nombre = s.replace(/\s*\/\s*/g, "/").replace(/\s+/g, " ").trim().toUpperCase();
  if (!nombre) return null;
  return { nombre, pantone: pantones.join(" / "), muestra };
}

// Los colores se capturan igual que las tallas: "NEGRO-BLANCO-ROJO" (o uno por
// renglon, como venia el archivo del cliente). El guion entre DIGITOS no separa,
// porque es el del pantone: "ROSA 14-1905 TCX" es un solo color.
export function partirColores(crudo) {
  const piezas = [];
  // tambien coma o punto y coma: "ROSA,AZUL,MORADO" entraba como un solo color
  txt(crudo).split(/\r?\n/).forEach((linea) => {
    let actual = "";
    for (let i = 0; i < linea.length; i++) {
      const c = linea[i];
      const esPantone = c === "-" && /\d/.test(linea[i - 1] || "") && /\d/.test(linea[i + 1] || "");
      if ((c === "-" && !esPantone) || c === "," || c === ";") { piezas.push(actual); actual = ""; }
      else actual += c;
    }
    piezas.push(actual);
  });
  return piezas.map((x) => txt(x)).filter(Boolean);
}

// "CH-MD-GD-EG" | "6M- 12M- 18M" | "12-14-16." -> ["CH","MD","GD","EG"]
export function escalaTallas(crudo) {
  return txt(crudo).split(/[-/,]/).map((x) => txt(x).replace(/\.$/, "").toUpperCase()).filter(Boolean);
}

// "1-2-2-1" | "1-2-2.2" | "1221" -> [1,2,2,1]
export function proporcion(crudo, cuantasTallas) {
  const s = txt(crudo);
  if (!s) return [];
  if (/[-.,]/.test(s)) return s.split(/[-.,]/).map((x) => nu(x)).filter((n) => n > 0);
  if (/^\d+$/.test(s) && s.length === cuantasTallas) return s.split("").map(Number);
  const n = nu(s);
  return n > 0 ? [n] : [];
}

// Reparte un total entre partes con restos mayores: la suma siempre cuadra.
export function reparte(total, pesos) {
  const suma = pesos.reduce((s, p) => s + p, 0);
  if (!suma || !pesos.length) return pesos.map(() => 0);
  const exactos = pesos.map((p) => (total * p) / suma);
  const base = exactos.map((x) => Math.floor(x));
  let resto = total - base.reduce((s, x) => s + x, 0);
  const orden = exactos.map((x, i) => ({ i, frac: x - Math.floor(x) })).sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < orden.length && resto > 0; k++, resto--) base[orden[k].i]++;
  return base;
}

// El renglon de ejemplo del layout se ignora aunque no lo borren: el layout
// nuevo lo marca con MODELO "EJEMPLO-BORRAR"; el anterior traia OPTIMA / 52882 /
// 1,800 piezas, y tambien se reconoce por si suben uno que ya tenian descargado.
const esEjemplo = (f) => {
  const modelo = norm(dame(f, "MODELO"));
  if (modelo.startsWith("EJEMPLO")) return true;
  return modelo === "52882" && norm(dame(f, "CLIENTE")) === "OPTIMA"
    && Math.round(nu(dame(f, "CANTIDAD"))) === 1800 && norm(dame(f, "REFERENCIA DE MUESTRA")) === "LN-2703";
};

// Devuelve { prepedidos, avisos }
export function leePrepedidos(filas) {
  const porPrepedido = new Map();
  const avisos = [];
  filas.forEach((f, idx) => {
    const renglon = idx + 2;
    if (esEjemplo(f)) return;
    const numero = txt(dame(f, "# PREPEDIDO"));
    const modelo = txt(dame(f, "MODELO"));
    const cantidad = Math.round(nu(dame(f, "CANTIDAD")));
    if (!numero && !modelo && !txt(dame(f, "CLIENTE"))) return;
    if (!modelo) { avisos.push({ renglon, aviso: "sin MODELO, se omite" }); return; }
    if (cantidad <= 0) { avisos.push({ renglon, modelo, aviso: "sin cantidad, se omite" }); return; }

    const colores = partirColores(dame(f, "COLOR")).map(limpiaColor).filter(Boolean);
    if (!colores.length) {
      colores.push({ nombre: "NA", pantone: "" });
      avisos.push({ renglon, modelo, aviso: "sin color, queda en NA" });
    }
    // Las tallas vienen como escala de texto o como columnas con cantidades
    let tallas = escalaTallas(dame(f, "TALLA"));
    let pesos = [];
    if (!tallas.length) {
      const porColumna = COLUMNAS_TALLA.map((c) => ({ talla: c, q: nu(dame(f, c)) })).filter((x) => x.q > 0);
      if (porColumna.length) {
        tallas = porColumna.map((x) => x.talla);
        pesos = porColumna.map((x) => x.q);
      }
    }
    if (!tallas.length) {
      tallas = ["NA"];
      avisos.push({ renglon, modelo, aviso: "sin escala de tallas, queda en NA" });
    }
    if (!pesos.length) {
      pesos = proporcion(dame(f, "PROPORCIÓN TALLA"), tallas.length);
      if (pesos.length !== tallas.length) {
        if (pesos.length) avisos.push({ renglon, modelo, aviso: `la proporcion (${pesos.length}) no cuadra con las tallas (${tallas.length}): se reparte parejo` });
        pesos = tallas.map(() => 1);
      }
    }

    // La proporcion de color reparte entre colores igual que la de talla entre
    // tallas; si no viene, los colores van parejos.
    let pesosColor = proporcion(dame(f, "PROPORCION COLOR"), colores.length);
    if (pesosColor.length !== colores.length) {
      if (pesosColor.length) avisos.push({ renglon, modelo, aviso: `la proporcion de color (${pesosColor.length}) no cuadra con los colores (${colores.length}): se reparte parejo` });
      pesosColor = colores.map(() => 1);
    }
    // El total del renglon se reparte por talla y cada talla entre los colores
    const porTalla = reparte(cantidad, pesos);
    const celdas = [];
    tallas.forEach((talla, i) => {
      const porColor = reparte(porTalla[i], pesosColor);
      colores.forEach((c, j) => {
        if (porColor[j] > 0) celdas.push({ color: c.nombre, pantone: c.pantone, talla, cantidad: porColor[j] });
      });
    });

    const art = {
      renglon,
      modelo,
      referencia: txt(dame(f, "REFERENCIA DE MUESTRA")),
      // mismos catalogos que el alta de modelo del sistema
      depto: txt(dame(f, "DEPARTAMENTO")) || txt(dame(f, "DEPTO")),
      linea: txt(dame(f, "LINEA")),
      coleccion: txt(dame(f, "COLECCION")),
      empresa: txt(dame(f, "EMPRESA")),
      prenda: txt(dame(f, "PRENDA")),
      composicion: txt(dame(f, "COMPOSICION")).replace(/\r?\n/g, " "),
      marca: txt(dame(f, "MARCA")),
      compradora: txt(dame(f, "COMPRADORA")),
      proveedor: txt(dame(f, "PROVEEDOR")),
      colores, tallas, proporcion: pesos, cantidad, celdas,
      costoFOB: nu(dame(f, "COSTO FOB")),
      costoDLLS: nu(dame(f, "COSTO DLLS")),
      costoCliente: nu(dame(f, "COSTO CLIENTE")),
      // "costo cliente" es el precio que se le cobra al cliente
      pvp: nu(dame(f, "PRECIO VENTA PUBLICO")) || nu(dame(f, "PVP")),
      oc: txt(dame(f, "OC")),
      // fechas ya normalizadas: la FECHA FOB es la fecha compromiso de la OC
      entrega: fechaISO(dame(f, "ENTREGA INICIAL")),
      fechaFOB: fechaISO(dame(f, "FECHA FOB")),
      comentarioCliente: txt(dame(f, "COMENTARIO CLIENTE")),
      comentarioInterno: txt(dame(f, "COMENTARIO INTERNO")),
      // lo demas del renglon tambien se guarda, no se pierde nada
      vendedor: txt(dame(f, "VENDEDOR")),
      ordenCompraCliente: txt(dame(f, "ORDEN DE COMPRA")) || txt(dame(f, "OC")),
      refColor: txt(dame(f, "REF COLOR")),
      costoMXN: nu(dame(f, "COSTO MXN")) || nu(dame(f, "COSTO MX")),
      unidad: txt(dame(f, "UN")),
      cancelacion: fechaISO(dame(f, "CANCELACION")),
      monarch: txt(dame(f, "MONARCH")),
      proporcionColor: txt(dame(f, "PROPORCION COLOR")),
      proporcionTalla: txt(dame(f, "PROPORCION TALLA")),
    };
    // El folio del prepedido lo pone el sistema. Los renglones se agrupan por
    // CLIENTE + ORDEN DE COMPRA del cliente; si llega un archivo viejo que
    // todavia trae "# PREPEDIDO", se respeta esa agrupacion.
    const ocCliente = txt(dame(f, "ORDEN DE COMPRA"));
    const grupo = numero || (ocCliente ? `OC ${ocCliente}` : "Sin OC");
    const llave = `${grupo}|${txt(dame(f, "CLIENTE"))}`;
    if (!porPrepedido.has(llave)) {
      porPrepedido.set(llave, {
        numero: grupo,
        // solo viene en archivos viejos; el nuevo layout ya no lo trae
        prepedidoArchivo: numero || "",
        cliente: txt(dame(f, "CLIENTE")),
        vendedor: txt(dame(f, "VENDEDOR")),
        ordenCompraCliente: ocCliente,
        proveedores: new Set(),
        articulos: [],
      });
    }
    const pp = porPrepedido.get(llave);
    pp.proveedores.add(art.proveedor);
    pp.articulos.push(art);
  });

  const prepedidos = [...porPrepedido.values()].map((p) => ({
    ...p,
    proveedores: [...p.proveedores].filter(Boolean),
    piezas: p.articulos.reduce((s, a) => s + a.cantidad, 0),
  }));
  return { prepedidos, avisos };
}

export default leePrepedidos;
